import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Check, Search, Users, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PickerBroker {
  user_id: string;
  display_name: string;        // primary line — company name preferred
  secondary_name?: string;     // person name (optional)
  network_ids?: string[];      // memberships across shipper's networks
}
export interface PickerNetwork {
  id: string;
  name: string;
}

type FilterMode = "all" | "selected" | "unselected";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title?: string;
  brokers: PickerBroker[];
  networks?: PickerNetwork[];                // optional network filter
  initialSelectedIds: string[];
  /** If true: when caller chooses everyone in a single-network scope, emit null.
   *  When false (Add-to-network mode), always emit the explicit array. */
  allowAllAsNull?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  applyLabel?: string;
  onApply: (selectedIds: string[] | null) => void;
}

function initialsOf(s: string): string {
  const parts = s.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function BrokerVisualPickerSheet({
  open, onOpenChange, title = "Select Brokers", brokers, networks = [],
  initialSelectedIds, allowAllAsNull = false, loading = false,
  emptyMessage = "No brokers available.",
  applyLabel = "Apply",
  onApply,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelectedIds));
  const [filter, setFilter] = useState<FilterMode>("all");
  const [networkFilter, setNetworkFilter] = useState<string>("__all__");
  const [query, setQuery] = useState("");

  // Reset when sheet reopens
  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelectedIds));
      setFilter("all");
      setNetworkFilter("__all__");
      setQuery("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return brokers.filter((b) => {
      if (filter === "selected" && !selected.has(b.user_id)) return false;
      if (filter === "unselected" && selected.has(b.user_id)) return false;
      if (networkFilter !== "__all__" && !(b.network_ids ?? []).includes(networkFilter)) return false;
      if (q) {
        const hay = `${b.display_name} ${b.secondary_name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [brokers, selected, filter, networkFilter, query]);

  const total = brokers.length;
  const allSelected = total > 0 && selected.size === total;

  const toggle = (id: string) => setSelected((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectAll = () => setSelected(new Set(brokers.map((b) => b.user_id)));
  const clear = () => setSelected(new Set());

  const handleApply = () => {
    const ids = Array.from(selected);
    if (allowAllAsNull && allSelected) onApply(null);
    else onApply(ids);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // full-screen on mobile, large modal on desktop
          "p-0 gap-0 max-w-none w-screen h-[100dvh] sm:h-[88vh] sm:w-[92vw] sm:max-w-3xl sm:rounded-lg",
          "flex flex-col"
        )}
      >
        {/* Header */}
        <div className="px-4 sm:px-5 py-3 border-b border-border flex items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Users className="h-4 w-4 text-brand-teal shrink-0" />
            <h2 className="text-sm font-bold uppercase tracking-wider truncate">{title}</h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-xs text-muted-foreground hidden sm:block">
              <span className="font-semibold text-foreground">{selected.size}</span> of {total} selected
            </div>
            <Button
              size="sm"
              className="h-8 text-white"
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              onClick={handleApply}
              disabled={loading}
            >
              {applyLabel} ({selected.size})
            </Button>
          </div>
        </div>

        {/* Sticky filter row */}
        <div className="px-4 sm:px-5 py-2.5 border-b border-border bg-card/40 space-y-2 shrink-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
              {(["all", "selected", "unselected"] as FilterMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setFilter(m)}
                  className={cn(
                    "px-3 py-1.5 capitalize transition-colors",
                    filter === m
                      ? "bg-brand-teal text-white"
                      : "bg-background hover:bg-muted text-foreground"
                  )}
                >
                  {m}
                </button>
              ))}
            </div>

            {networks.length > 1 && (
              <Select value={networkFilter} onValueChange={setNetworkFilter}>
                <SelectTrigger className="h-8 w-[180px] text-xs">
                  <SelectValue placeholder="Network" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All networks</SelectItem>
                  {networks.map((n) => (
                    <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <div className="ml-auto flex items-center gap-1">
              <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={selectAll} disabled={!brokers.length}>
                Select all
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={clear} disabled={!selected.size}>
                Clear
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search brokers (optional)"
              className="h-8 border-0 shadow-none focus-visible:ring-0 px-0 text-sm"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="p-1 hover:bg-muted rounded-sm">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3">
          {loading ? (
            <div className="text-sm text-muted-foreground text-center py-12">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-12">
              {brokers.length === 0 ? emptyMessage : "No brokers match the current filter."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
              {filtered.map((b) => {
                const isSel = selected.has(b.user_id);
                return (
                  <button
                    key={b.user_id}
                    type="button"
                    onClick={() => toggle(b.user_id)}
                    className={cn(
                      "relative text-left rounded-lg border p-3 min-h-[88px] transition-all",
                      "flex flex-col gap-2 items-start",
                      isSel
                        ? "border-brand-teal bg-brand-teal/10 ring-2 ring-brand-teal"
                        : "border-border bg-card hover:bg-muted/50"
                    )}
                  >
                    {isSel && (
                      <span className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-brand-teal text-white flex items-center justify-center">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                    <div className="flex items-center gap-2 w-full min-w-0">
                      <div
                        className={cn(
                          "h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0",
                          isSel ? "bg-brand-teal text-white" : "bg-brand-teal/15 text-brand-teal"
                        )}
                      >
                        {initialsOf(b.display_name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-foreground truncate">{b.display_name}</div>
                        {b.secondary_name && (
                          <div className="text-[11px] text-muted-foreground truncate">{b.secondary_name}</div>
                        )}
                      </div>
                    </div>
                    {(b.network_ids?.length ?? 0) > 1 && (
                      <Badge variant="outline" className="text-[10px] h-5">
                        in {b.network_ids!.length} networks
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-3 border-t border-border flex items-center justify-between gap-2 shrink-0">
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{selected.size}</span> of {total} selected
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-9 text-white"
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              onClick={handleApply}
              disabled={loading}
            >
              {applyLabel} ({selected.size})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
